import React, { memo } from "react";
import { Box } from "@mui/material";
import {
  BRAND,
  GREY,
  BASE,
  BORDER,
  ERROR,
} from "../../../../../utils/constants/colors";
import { fontWeight } from "../../../../../utils/constants/UI";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import { sharedBillingStyles as s, formatDate } from "./billingUtils";
import {
  isRestrictedTenant,
  isAutoTopUpEnabledTenant,
} from "../../../../../utils/helpers/tenantRestrictions";

/**
 * BillingSettingsSection
 *
 * Auto top-up toggle and danger zone (subscription cancellation).
 */
const BillingSettingsSection = memo(
  ({
    toggles,
    handleToggle,
    loadingCancelInfo,
    handleCancelClick,
    currentSubscription,
    handleResumeSubscription,
    isResuming,
    handleEndTrial,
    isEndingTrial,
    handleManagePayment,
    isManagingPayment,
  }) => {
    // Restricted tenants have Auto-Top-up greyed out, except the tenants
    // explicitly allowed to keep it enabled.
    const autoTopUpDisabled =
      isRestrictedTenant() && !isAutoTopUpEnabledTenant();
    // Fix R4 (A8): the auto-topup BE endpoint was removed (it 404s now) so the toggle is a dead
    // control — hide the whole Auto-Top-up card for everyone. (Fully removing the card markup +
    // fetchAutoTopUp/handleToggle/endpoint is optional follow-up cleanup.)
    const hideAutoTopUp = true;
    // Subscription lifecycle state (drives the dunning + cancel-scheduled UI).
    const subStatus = currentSubscription?.status;
    const isPastDue = subStatus === "past_due";
    const isScheduledCancel = !!currentSubscription?.cancel_at_period_end;
    const periodEndLabel = currentSubscription?.current_period_end
      ? formatDate(currentSubscription.current_period_end)
      : "the end of your billing period";
    const isTrialing = subStatus === "trialing";
    const trialEndLabel = currentSubscription?.trial_end
      ? formatDate(currentSubscription.trial_end)
      : periodEndLabel;
    return (
    <Box style={s.contentWrapper}>
      <Box style={{ display: "grid", gridTemplateColumns: "1fr", gap: "24px" }}>
        <Box style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
          {/* Auto top-up card */}
          <Box
            style={{
              ...s.card,
              display: hideAutoTopUp ? "none" : undefined,
              opacity: autoTopUpDisabled ? 0.6 : 1,
              pointerEvents: autoTopUpDisabled ? "none" : "auto",
              userSelect: autoTopUpDisabled ? "none" : "auto",
            }}
            aria-disabled={autoTopUpDisabled || undefined}
          >
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "10px",
                flexWrap: "wrap",
              }}
            >
              <Box>
                <TypographyInline
                  size="md"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={GREY.PRIMARY}
                  label="Auto-Top-up"
                />
                <Box style={{ marginTop: "5px" }}>
                  <TypographyInline
                    size="xs"
                    color={GREY.TERTIARY}
                    label="Control automated credit purchasing."
                  />
                </Box>
              </Box>
              {autoTopUpDisabled && (
                <span
                  style={{
                    ...s.badge,
                    background: BORDER.DISABLED,
                    color: GREY.SECONDARY,
                  }}
                >
                  Not available on your workspace
                </span>
              )}
            </Box>
            <Box
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "16px",
                marginTop: "16px",
                padding: "16px",
                background: BASE.BACKGROUND_LIGHT,
                borderRadius: "10px",
                border: `1px solid ${BORDER.DEFAULT}`,
              }}
              sx={{
                flexDirection: {
                  xs: "column",
                  sm: "row",
                },
                alignItems: {
                  xs: "flex-start",
                  sm: "center",
                },
              }}
            >
              <Box>
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.MEDIUM}
                  color={GREY.PRIMARY}
                  label="Automatic Plan Renewal"
                />
                <Box style={{ marginTop: "4px" }}>
                  <TypographyInline
                    size="xs"
                    color={GREY.TERTIARY}
                    label="When enabled, your currently selected plan will be automatically renewed and debited from your primary payment method to ensure uninterrupted service."
                  />
                </Box>
              </Box>
              {/* Toggle */}
              <Box
                style={{
                  ...s.toggleTrack,
                  background:
                    !autoTopUpDisabled && toggles.autoPurchase
                      ? BRAND.PRIMARY
                      : BORDER.DEFAULT,
                  borderColor:
                    !autoTopUpDisabled && toggles.autoPurchase
                      ? BRAND.PRIMARY
                      : BORDER.DARK,
                  cursor: autoTopUpDisabled ? "not-allowed" : "pointer",
                }}
                onClick={
                  autoTopUpDisabled ? undefined : () => handleToggle("autoPurchase")
                }
                onKeyDown={
                  autoTopUpDisabled
                    ? undefined
                    : (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleToggle("autoPurchase");
                        }
                      }
                }
                role="switch"
                aria-checked={
                  autoTopUpDisabled ? false : toggles.autoPurchase
                }
                aria-disabled={autoTopUpDisabled || undefined}
                aria-label="Automatic plan renewal"
                tabIndex={autoTopUpDisabled ? -1 : 0}
                sx={
                  autoTopUpDisabled
                    ? {}
                    : {
                        "&:hover": {
                          borderColor: toggles.autoPurchase
                            ? BRAND.PRIMARY
                            : BORDER.HOVER,
                        },
                        "&:active": {
                          boxShadow: `0 8px 16px -18px ${GREY.SECONDARY}`,
                        },
                        "&:focus-visible": {
                          outline: `2px solid ${BRAND.PRIMARY}`,
                          outlineOffset: "2px",
                        },
                      }
                }
              >
                <Box
                  style={{
                    ...s.toggleKnob,
                    transform:
                      !autoTopUpDisabled && toggles.autoPurchase
                        ? "translateX(22px)"
                        : "translateX(0px)",
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Danger Zone */}
          <Box
            style={{
              ...s.dangerZone,
              border: `1px solid ${ERROR.W_200}`,
              background: ERROR.W_50,
            }}
          >
            {/* The billing-cycle cancellation copy only makes sense for a PAID subscription.
                During the 7-day free trial it is hidden (trial has its own copy below); it
                reappears automatically once the trial converts to paid. */}
            {!isTrialing && (
              <>
                <TypographyInline
                  size="sm"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={ERROR.W_700}
                  label="Subscription Cancellation"
                />
                <Box style={{ marginTop: "8px" }}>
                  <TypographyInline
                    size="xs"
                    color={ERROR.W_600}
                    label="If you cancel, your plan stays active until the end of the current billing cycle — all active campaigns keep running until then. After that your workspace moves to the Free plan (credits reset to the free tier). Account data is retained for 30 days, after which it may be permanently removed."
                  />
                </Box>
              </>
            )}

            {/* Payment failed (dunning) — let the user fix their card before the plan lapses. */}
            {isPastDue && (
              <Box
                style={{
                  marginTop: "12px",
                  padding: "10px 12px",
                  borderRadius: "8px",
                  border: `1px solid ${ERROR.W_200}`,
                  background: ERROR.W_50,
                }}
              >
                <TypographyInline
                  size="xs"
                  fontWeight={fontWeight.SEMIBOLD}
                  color={ERROR.W_700}
                  label="Your last payment failed. Please update your card to avoid losing your plan."
                />
                <Box style={{ marginTop: "10px", width: "fit-content" }}>
                  <Button
                    label="Update payment method"
                    variant="primary"
                    size="sm36"
                    isLoading={isManagingPayment}
                    onClick={handleManagePayment}
                  />
                </Box>
              </Box>
            )}

            {isTrialing && (
              /* On a trial → convert early ("Bill me now") or cancel — both actions side by side. */
              <Box>
                <TypographyInline
                  size="xs"
                  color={GREY.SECONDARY}
                  label={
                    isScheduledCancel
                      ? `Your free trial is cancelled — it ends ${trialEndLabel} and you won't be charged. Changed your mind? Start your subscription now to keep the plan (billing starts immediately).`
                      : `You're on a free trial — it ends ${trialEndLabel}. Start your subscription now to unlock your full monthly credits right away.`
                  }
                />
                <Box style={{ marginTop: "12px", display: "flex", gap: "12px", alignItems: "center", width: "fit-content" }}>
                  <Button
                    label="Start subscription now"
                    variant="primary"
                    size="sm36"
                    isLoading={isEndingTrial}
                    onClick={handleEndTrial}
                  />
                  {!isScheduledCancel && (
                    <Button
                      label="Cancel free trial"
                      variant="primary"
                      size="sm36"
                      backgroundColor={ERROR.W_600}
                      isLoading={loadingCancelInfo}
                      onClick={handleCancelClick}
                    />
                  )}
                </Box>
              </Box>
            )}

            {isScheduledCancel && !isTrialing ? (
              /* PAID sub scheduled to cancel at period end → offer reactivate, not cancel.
                 A cancelled TRIAL shows only "Start subscription now" above (one button —
                 end-trial now also clears the scheduled cancel on the Stripe side). */
              <Box style={{ marginTop: "12px" }}>
                <TypographyInline
                  size="xs"
                  color={ERROR.W_600}
                  label={`Your subscription is scheduled to cancel — you keep full access until ${periodEndLabel}, after which your workspace reverts to the Free plan.`}
                />
                <Box style={{ marginTop: "16px", width: "fit-content" }}>
                  <Button
                    label="Reactivate subscription"
                    variant="primary"
                    size="sm36"
                    isLoading={isResuming}
                    onClick={handleResumeSubscription}
                  />
                </Box>
              </Box>
            ) : (
              /* Trialing renders its cancel button inline next to "Start subscription now"
                 (above); this standalone cancel is for paid subscriptions only. */
              !isTrialing && (
                <Box style={{ marginTop: "16px", width: "fit-content" }}>
                  <Button
                    label="Cancel Subscription"
                    variant="primary"
                    size="sm36"
                    backgroundColor={ERROR.W_600}
                    isLoading={loadingCancelInfo}
                    onClick={handleCancelClick}
                  />
                </Box>
              )
            )}
          </Box>
        </Box>
      </Box>
    </Box>
    );
  },
);

BillingSettingsSection.displayName = "BillingSettingsSection";

export default BillingSettingsSection;
